import {ref} from "vue";
import {useMutation, useQuery, useQueryClient} from "@tanstack/vue-query";
import {errorToast, successToast} from "@/composables/useToasts";

export const PagesTabs = Object.freeze({
  PRIORITISED: 'PRIORITISED',
  AVAILABLE: 'AVAILABLE',
  UPDATED: 'UPDATED',
});

export const activeTab = ref(PagesTabs.PRIORITISED);

export const availablePages = ref([]);

export const prioritisedPages = ref([]);

const assignMonthIndexes = (data = []) => {
  let index = 0;

  /**
   * Maintains the priority order of pages in the list whilst adding a month index for the UI to calculate which
   * month the page will be processed in.
   */
  return data.map((kitPage) => {
    const hasFixableActions = kitPage.has_kit_fixable_actions;
    const page = {
      ...kitPage,
      monthIndex: hasFixableActions ? index : null,
    };

    if (hasFixableActions){
      index++;
    }

    return page;
  });
};

export const useKitPages = () => {
  const queryClient = useQueryClient();

  const getAvailablePages = () => useQuery({
    queryKey: ['available-pages'],
    queryFn: async () => {
      const {data} = await axios.get('/ajax/v2/seo-agent/available-pages');

      availablePages.value = data.data;
      return data.data;
    }
  });

  const getPrioritisedPages = () => useQuery({
    queryKey: ['prioritised-pages'],
    queryFn: async () => {
      const {data} = await axios.get('/ajax/v2/seo-agent/prioritised-pages');

      setPrioritisedPages(data.data);
      return data.data;
    }
  });

  const addPriorityPage = () => useMutation({
    mutationFn: async (pageId) => await axios.post(`/ajax/v2/seo-agent/add-priority-page/${pageId}`),
    onSuccess: (_, pageId) => {
      availablePages.value = availablePages.value.filter((page) => page.id !== pageId);

      queryClient.invalidateQueries({queryKey: ['available-pages']});
      queryClient.invalidateQueries({queryKey: ['prioritised-pages']});
      successToast('Page added to the priority list.');
    },
    onError: () => {
      errorToast('Something went wrong adding the page to the priority list.');
    }
  });

  const removePriorityPage = () => useMutation({
    mutationFn: async (pageId) => await axios.post(`/ajax/v2/seo-agent/remove-priority-page/${pageId}`),
    onSuccess: (_, pageId) => {
      setPrioritisedPages(prioritisedPages.value.filter((page) => page.page_id !== pageId));

      queryClient.invalidateQueries({queryKey: ['available-pages']});
      queryClient.invalidateQueries({queryKey: ['prioritised-pages']});
      successToast('Page removed from the priority list.');
    },
    onError: () => {
      errorToast('Something went wrong removing the page from the priority list.');
    }
  });

  const updatePagePriorities = () => useMutation({
    mutationFn: async () => {
      const pages = prioritisedPages.value.map((page, index) => ({
        page_id: page.page_id,
        priority: index + 1,
      }));

      await axios.post('/ajax/v2/seo-agent/update-page-priorities', {pages});
    },
    onMutate: async () => {
      const previous = prioritisedPages.value.map((page) => ({...page}));
      return {previous};
    },
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['available-pages']});
      queryClient.invalidateQueries({queryKey: ['prioritised-pages']});
    },
    onError: (_, __, context) => {
      if (context?.previous) {
        setPrioritisedPages(context.previous);
      }
      errorToast('Something went wrong updating the page priorities.');
    }
  });

  const setPrioritisedPages = (data) => prioritisedPages.value = assignMonthIndexes(data);

  const recalculatePrioritisedPageIndexes = () => prioritisedPages.value = assignMonthIndexes(prioritisedPages.value);

  return {
    getAvailablePages,
    getPrioritisedPages,
    addPriorityPage,
    removePriorityPage,
    updatePagePriorities,
    recalculatePrioritisedPageIndexes,
  };
};


